import { useEffect, useRef, useState } from 'react';
import type { ChatMessage } from '@/mocks/chat';
import HostedCreditsCTA from './HostedCreditsCTA';
import MarkdownPreview from './MarkdownPreview';
import type { HostedCreditsMode } from '../hostedCredits';
import type { PlaybookEditCard } from '../playbookEdits';
import type { HistoryRow } from '../playbookHistory';
import { scrollTranscriptToEnd } from '../chatScroll';

const STARTERS = [
  'Why did the latest score drop?',
  'What should we keep or drop in the playbook?',
  'Is the model or the context the bottleneck?',
];

type Props = {
  messages: ChatMessage[];
  onSend: (content: string) => void;
  typing: boolean;
  error?: string | null;
  hostedCredits?: HostedCreditsMode;
  modelLabel?: string;
  playbookOps?: PlaybookEditCard[];
  history?: HistoryRow[];
  onApplyPlaybook?: () => void;
  onFork?: (name: string) => void;
  onRestore?: (entryId: string) => void;
  onApplyCompose?: (recommendations: NonNullable<ChatMessage['recommendations']>) => void;
};

export default function AnalyzeChat({
  messages, onSend, typing, error, hostedCredits, modelLabel,
  playbookOps = [], history = [], onApplyPlaybook, onFork, onRestore, onApplyCompose,
}: Props) {
  const [input, setInput] = useState('');
  const [forkName, setForkName] = useState('');
  const transcriptRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollTranscriptToEnd(transcriptRef.current);
  }, [messages.length, typing]);

  const submit = () => {
    if (hostedCredits) return;
    const v = input.trim();
    if (!v) return;
    onSend(v);
    setInput('');
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden bg-bg" data-testid="analyze-chat">
      {modelLabel ? (
        <div className="h-8 shrink-0 border-b border-border flex items-center px-3">
          <div className="text-[10px] font-mono text-texts truncate" title={modelLabel}>{modelLabel}</div>
        </div>
      ) : null}

      <div ref={transcriptRef} className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-3">
        {messages.length === 0 && !typing && (
          <div className="text-texts">
            <div className="text-xs text-textp font-medium">Chat about this run</div>
            <div className="text-[11px] mt-1 leading-snug">
              I can see the outcome, project files, playbook, scores, and models. Ask what to compress or keep.
            </div>
            {hostedCredits ? (
              <div className="mt-3">
                <HostedCreditsCTA mode={hostedCredits} />
              </div>
            ) : (
              <div className="mt-3 flex flex-col gap-1.5">
                {STARTERS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => onSend(s)}
                    className="text-left text-[11px] bg-surface hover:bg-surface2 border border-border rounded-md px-2.5 py-1.5 text-textp"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {messages.map((m) => (
          <AnalyzeMessage key={m.id} msg={m} onApplyCompose={onApplyCompose} />
        ))}
        {playbookOps.length > 0 && (
          <div data-testid="analyze-playbook-ops" className="bg-surface border border-border rounded-lg px-3 py-2.5">
            <div className="text-[11px] font-medium text-textp mb-1.5">Playbook edits from Analyze</div>
            <ul className="space-y-1 mb-2">
              {playbookOps.map((e, i) => (
                <li key={`${e.skill_id}-${e.content}-${i}`} className="text-[11px] text-textp">
                  <span className="font-mono text-texts mr-1">{e.label}</span>
                  <span className="text-texts">{e.skill_id}:</span> {e.content}
                </li>
              ))}
            </ul>
            <button
              type="button"
              data-testid="analyze-playbook-apply"
              onClick={() => onApplyPlaybook?.()}
              className="bg-accent hover:bg-accent-hover text-accent-text text-xs font-medium px-2.5 py-1.5 rounded-md"
            >
              Apply
            </button>
          </div>
        )}
        {history.length > 0 && (
          <ul data-testid="playbook-history" className="space-y-1.5 pt-1">
            {history.map((row) => (
              <li key={row.id} className="flex items-center justify-between gap-2 text-[11px] text-textp">
                <span className="truncate">{row.label}</span>
                <button
                  type="button"
                  onClick={() => onRestore?.(row.id)}
                  className="shrink-0 text-texts hover:text-textp px-1.5 py-1 rounded-md"
                >
                  Restore
                </button>
              </li>
            ))}
          </ul>
        )}
        {typing && (
          <div className="bg-surface border border-border rounded-lg rounded-bl-sm px-3 py-2.5 inline-flex items-center gap-1.5">
            <span className="typing-dot" />
            <span className="typing-dot" />
            <span className="typing-dot" />
          </div>
        )}
        {hostedCredits && messages.length > 0 && (
          <HostedCreditsCTA mode={hostedCredits} />
        )}
        {error && !hostedCredits && (
          <div data-testid="analyze-chat-error" className="text-[11px] text-error bg-error/10 border border-error/30 rounded-md px-2.5 py-2">{error}</div>
        )}
      </div>

      <div className="shrink-0 border-t border-border p-2 space-y-1.5">
        <div className="flex items-end gap-1.5">
          <input
            data-testid="playbook-fork-name"
            value={forkName}
            onChange={(e) => setForkName(e.target.value)}
            placeholder="Fork name"
            className="flex-1 bg-surface border border-border rounded-md px-2.5 py-1.5 text-xs text-textp placeholder:text-texts outline-none focus:border-accent"
          />
          <button
            type="button"
            data-testid="playbook-fork"
            onClick={() => {
              if (!forkName.trim()) return;
              onFork?.(forkName);
            }}
            className="whitespace-nowrap text-xs px-2.5 py-1.5 rounded-md border border-border text-textp hover:bg-surface2"
          >
            Fork
          </button>
        </div>
        <div className="flex items-end gap-1.5">
          <textarea
            data-testid="analyze-chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            rows={2}
            placeholder={hostedCredits === 'login' ? 'Sign in to send' : hostedCredits ? 'Buy credits to send' : 'Ask about scores, playbook, models…'}
            disabled={!!hostedCredits}
            className="flex-1 bg-surface border border-border rounded-md px-2.5 py-1.5 text-xs text-textp placeholder:text-texts outline-none focus:border-accent resize-none disabled:opacity-50"
          />
          <button
            data-testid="analyze-chat-send"
            onClick={submit}
            disabled={!input.trim() || typing || !!hostedCredits}
            className="whitespace-nowrap flex items-center justify-center w-8 h-8 rounded-md bg-accent hover:bg-accent-hover disabled:opacity-40 text-accent-text shrink-0"
            title="Send"
          >
            <i className="ri-send-plane-2-line text-sm" />
          </button>
        </div>
      </div>
    </div>
  );
}

function AnalyzeMessage({
  msg,
  onApplyCompose,
}: {
  msg: ChatMessage;
  onApplyCompose?: (recommendations: NonNullable<ChatMessage['recommendations']>) => void;
}) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className="max-w-[92%]">
        <div className={`rounded-lg px-2.5 py-2 text-xs leading-relaxed ${
          isUser ? 'bg-accent text-accent-text rounded-br-sm whitespace-pre-wrap' : 'bg-surface border border-border text-textp rounded-bl-sm'
        }`}
        >
          {isUser ? msg.content : <MarkdownPreview content={msg.content} compact />}
        </div>
        {!isUser && msg.recommendations && msg.recommendations.length > 0 && (
          <div data-testid="analyze-compose-mockup" className="mt-2 rounded-lg border border-border bg-surface px-3 py-2.5">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="text-[11px] font-medium text-texts uppercase tracking-wide">Compose mockup</div>
              {onApplyCompose && (
                <button
                  type="button"
                  data-testid="analyze-compose-add-to-canvas"
                  onClick={() => onApplyCompose(msg.recommendations!)}
                  className="whitespace-nowrap flex items-center gap-1 text-[11px] font-medium text-accent hover:text-accent-hover"
                >
                  <i className="ri-add-circle-line" />
                  Add to canvas
                </button>
              )}
            </div>
            <ol className="space-y-2">
              {msg.recommendations.map((r, i) => (
                <li key={r.id} className="text-xs">
                  <div className="font-medium text-textp">{i + 1}. {r.name}</div>
                  <div className="text-[11px] text-texts mt-0.5">{r.why}</div>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}
